import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { 
  Upload, 
  Download, 
  CheckCircle, 
  AlertTriangle,
  Users,
  FileSpreadsheet,
  ArrowRight,
  X
} from 'lucide-react';
import { toast } from "sonner";

export default function BulkUploadPage() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const downloadTemplate = () => {
    // Create sample Excel data
    const template = [
      ['Student Name', 'Year', 'Stream', 'Email', 'Password', 'Parent Name', 'Parent Phone', 'Relationship'],
      ['John Doe', '11', 'Science', 'john.doe@student.spc.edu.vu', 'john2026', 'Mrs. Doe', '7752345', 'Mother'],
      ['Jane Smith', '12', 'Arts', 'jane.smith@student.spc.edu.vu', 'jane2026', 'Mr. Smith', '7753456', 'Father'],
      ['Bob Wilson', '13', 'Commerce', 'bob.wilson@student.spc.edu.vu', 'bob2026', 'Mrs. Wilson', '7754567', 'Mother']
    ];

    // Convert to CSV
    const csv = template.map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'student_parent_upload_template.csv';
    a.click();
    
    toast.success('Template downloaded successfully');
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (selectedFile) => {
    const validTypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    
    if (!validTypes.includes(selectedFile.type) && !selectedFile.name.match(/\.(csv|xlsx|xls)$/)) {
      toast.error('Please upload a valid CSV or Excel file');
      return;
    }

    setFile(selectedFile);
    toast.success(`File "${selectedFile.name}" loaded`);
  };

  const processUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    setProgress(0);
    setResults(null);

    try {
      // Read file
      const text = await file.text();
      const rows = text.split('\n').map(row => row.split(','));
      
      // Remove header
      const header = rows[0];
      const data = rows.slice(1).filter(row => row.length > 1 && row[0].trim());

      // Simulate progress
      const total = data.length;
      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      for (let i = 0; i < data.length; i++) {
        const row = data[i];
        
        // Validate row
        if (row.length < 8) {
          errorCount++;
          errors.push(`Row ${i + 2}: Missing columns`);
          continue;
        }

        const [studentName, year, stream, email, password, parentName, parentPhone, relationship] = row.map(cell => cell.trim());

        // Validate required fields
        if (!studentName || !year || !email || !password || !parentPhone) {
          errorCount++;
          errors.push(`Row ${i + 2}: Missing required fields`);
          continue;
        }

        // Store in localStorage (in production, this would be backend API call)
        const studentId = `student-${year}-${Date.now()}-${i}`;
        const parentId = `parent-${Date.now()}-${i}`;

        const studentData = {
          id: studentId,
          name: studentName,
          year: parseInt(year),
          stream,
          email,
          password,
          role: 'student',
          createdAt: new Date().toISOString()
        };

        const parentData = {
          id: parentId,
          phone: parentPhone,
          pin: '2026', // Default PIN
          studentId,
          parentName: parentName || 'Parent',
          relationship: relationship || 'Guardian',
          status: 'active'
        };

        // Save to localStorage (temporary - would be database in production)
        const existingStudents = JSON.parse(localStorage.getItem('bulk_uploaded_students') || '[]');
        const existingParents = JSON.parse(localStorage.getItem('bulk_uploaded_parents') || '[]');
        
        existingStudents.push(studentData);
        existingParents.push(parentData);
        
        localStorage.setItem('bulk_uploaded_students', JSON.stringify(existingStudents));
        localStorage.setItem('bulk_uploaded_parents', JSON.stringify(existingParents));

        successCount++;
        setProgress(Math.round(((i + 1) / total) * 100));
        
        // Simulate processing delay
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      setResults({
        total: data.length,
        success: successCount,
        errors: errorCount,
        errorDetails: errors
      });

      if (successCount > 0) {
        toast.success(`Successfully uploaded ${successCount} student(s) and parent(s)`);
      }
      if (errorCount > 0) {
        toast.warning(`${errorCount} row(s) had errors`);
      }

    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to process file: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const clearFile = () => {
    setFile(null);
    setResults(null);
    setProgress(0);
  };

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Bulk Student & Parent Upload</h1>
        <p className="text-gray-600">
          Upload students and parent accounts for Term 1 2026 using Excel or CSV
        </p>
      </div>

      {/* Instructions Card */}
      <Card className="mb-6 border-blue-200 bg-blue-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-900">
            <FileSpreadsheet className="h-5 w-5" />
            How to Use
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="space-y-2 text-sm text-blue-800">
            <li className="flex items-start gap-2">
              <span className="font-bold">1.</span>
              <span>Download the Excel template below</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold">2.</span>
              <span>Fill in student details, year, stream, email, password</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold">3.</span>
              <span>Add parent name, phone number, and relationship</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold">4.</span>
              <span>Upload the completed file and click "Process Upload"</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold">5.</span>
              <span>Parents will receive SMS notifications with login credentials</span>
            </li>
          </ol>
        </CardContent>
      </Card>

      {/* Download Template */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Step 1: Download Template</CardTitle>
          <CardDescription>
            Download the Excel template and fill in student and parent information
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={downloadTemplate} size="lg">
            <Download className="h-4 w-4 mr-2" />
            Download Excel Template
          </Button>
        </CardContent>
      </Card>

      {/* Upload Area */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Step 2: Upload Completed File</CardTitle>
          <CardDescription>
            Drag and drop your file or click to browse
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {file ? (
              <div className="space-y-4">
                <div className="flex items-center justify-center gap-3">
                  <FileSpreadsheet className="h-8 w-8 text-green-600" />
                  <div className="text-left">
                    <p className="font-semibold text-gray-900">{file.name}</p>
                    <p className="text-sm text-gray-600">{(file.size / 1024).toFixed(2)} KB</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFile}
                    disabled={uploading}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <Upload className="h-12 w-12 mx-auto text-gray-400" />
                <div>
                  <p className="text-lg font-medium text-gray-900 mb-2">
                    Drop your file here, or click to browse
                  </p>
                  <p className="text-sm text-gray-600">
                    Supports CSV, XLS, XLSX files
                  </p>
                </div>
                <input
                  type="file"
                  onChange={handleFileInput}
                  accept=".csv,.xlsx,.xls"
                  className="hidden"
                  id="file-upload"
                />
                <label htmlFor="file-upload">
                  <Button asChild>
                    <span>Browse Files</span>
                  </Button>
                </label>
              </div>
            )}
          </div>

          {file && !uploading && !results && (
            <div className="mt-6">
              <Button onClick={processUpload} size="lg" className="w-full">
                <ArrowRight className="h-4 w-4 mr-2" />
                Process Upload
              </Button>
            </div>
          )}

          {uploading && (
            <div className="mt-6 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Processing...</span>
                <span className="font-semibold">{progress}%</span>
              </div>
              <Progress value={progress} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results */}
      {results && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Upload Complete
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-900">{results.total}</div>
                <div className="text-sm text-gray-600">Total Rows</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{results.success}</div>
                <div className="text-sm text-green-700">Successful</div>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600">{results.errors}</div>
                <div className="text-sm text-red-700">Errors</div>
              </div>
            </div>

            {results.errorDetails && results.errorDetails.length > 0 && (
              <Alert className="bg-red-50 border-red-300">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">
                  <p className="font-semibold mb-2">Errors found:</p>
                  <ul className="text-sm space-y-1 list-disc list-inside">
                    {results.errorDetails.slice(0, 5).map((error, i) => (
                      <li key={i}>{error}</li>
                    ))}
                    {results.errorDetails.length > 5 && (
                      <li>...and {results.errorDetails.length - 5} more errors</li>
                    )}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            <div className="flex gap-4">
              <Button onClick={clearFile} variant="outline">
                Upload Another File
              </Button>
              <Button onClick={() => window.location.reload()}>
                Done
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
